<template>
  <div class="py-4 container-fluid">
    <div class="mt-4 row">
      <div class="col-12">
        <div class="card">
          <div class="card-header border-bottom">
            <h5 class="mb-0">Calls History</h5>
            <small>Filtered by month: <strong>{{ selectedMonth }}</strong></small>
          </div>

          <!-- Controls Row -->
          <div class="d-flex align-items-center my-3 px-3">
            <!-- Export button aligned left -->
            <div class="export-button-container">
              <button class="btn btn-primary" @click="exportCSV">Export</button>
            </div>

            <!-- Month picker centered -->
            <div class="flex-grow-1 d-flex justify-content-center">
              <input
                type="month"
                v-model="selectedMonth"
                class="form-control"
                style="max-width: 200px;"
                @change="fetchCallsCount"
              />
            </div>

            <!-- Search aligned right -->
            <div class="search-container">
              <input
                type="text"
                class="form-control"
                placeholder="Search..."
                v-model="searchTerm"
              />
            </div>
          </div>

          <div class="table-responsive">
            <table class="table table-flush table-bordered text-center">
              <thead class="thead-light">
                <tr>
                  <th>Company Name</th>
                  <th>Phonebot</th>
                  <th>Number of Calls</th>
                </tr>
              </thead>
              <tbody>
                <template v-if="paginatedCompanies.length === 0">
                  <tr>
                    <td colspan="3" class="text-center">No users found.</td>
                  </tr>
                </template>

                <template v-for="(group, index) in paginatedCompanies" :key="index">
                  <tr v-for="(bot, botIndex) in group.phonebots" :key="botIndex">
                    <template v-if="botIndex === 0">
                      <td :rowspan="group.phonebots.length">{{ group.name }}</td>
                    </template>
                    <td>{{ bot.phonebot }}</td>
                    <td>{{ bot.number_of_calls }}</td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>

          <div class="card-footer d-flex justify-content-end">
            <nav>
              <ul class="pagination mb-0">
                <li class="page-item" :class="{ disabled: currentPage === 1 }">
                  <button class="page-link" @click="changePage(currentPage - 1)">Previous</button>
                </li>
                <li
                  class="page-item"
                  v-for="page in totalPages"
                  :key="page"
                  :class="{ active: page === currentPage }"
                >
                  <button class="page-link" @click="changePage(page)">{{ page }}</button>
                </li>
                <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                  <button class="page-link" @click="changePage(currentPage + 1)">Next</button>
                </li>
              </ul>
            </nav>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
import userService from "@/services/users.service";

export default {
  name: 'CallsHistory',

  data() {
    return {
      companies: [],
      searchTerm: '',
      currentPage: 1,
      perPage: 10,
      selectedMonth: '', // e.g. '2025-06'
      pollingInterval: null,
    };
  },

  mounted() {
    this.selectedMonth = new Date().toISOString().slice(0, 7); // Default: current month
    this.fetchCallsCount();

    this.pollingInterval = setInterval(() => {
      this.fetchCallsCount();
    }, 10000); // refresh every 10 seconds
  },

  beforeUnmount() {
    clearInterval(this.pollingInterval);
  },

  computed: {
    // Group users by name and filter by search term
    groupedCompanies() {
      const grouped = {};
      this.companies.forEach(company => {
        const nameLower = company.name?.toLowerCase() || '';
        const phonebotLower = company.phonebot?.toLowerCase() || '';
        const search = this.searchTerm.toLowerCase();

        const matches = !this.searchTerm || nameLower.includes(search) || phonebotLower.includes(search);
        if (!matches) return;

        if (!grouped[company.name]) {
          grouped[company.name] = {
            name: company.name || 'Unknown',
            phonebots: []
          };
        }

        grouped[company.name].phonebots.push({
          phonebot: company.phonebot || 'Unknown',
          number_of_calls: company.number_of_calls || 0
        });
      });

      return Object.values(grouped);
    },

    // Pagination for grouped users
    paginatedCompanies() {
      const start = (this.currentPage - 1) * this.perPage;
      return this.groupedCompanies.slice(start, start + this.perPage);
    },

    totalPages() {
      return Math.ceil(this.groupedCompanies.length / this.perPage) || 1;
    }
  },

  methods: {
    async fetchCallsCount() {
      try {
        const response = await userService.fetchCallsCount(this.selectedMonth);
        this.companies = response;
        this.currentPage = 1; // reset page when data changes
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    },

    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
      }
    },

    exportCSV() {
      if (!this.companies.length) {
        alert('No data to export');
        return;
      }

      // Export only filtered & grouped users
      const rows = [];
      this.groupedCompanies.forEach(group => {
        group.phonebots.forEach(bot => {
          rows.push({
            'Customer Name': group.name,
            'Phonebot': bot.phonebot,
            'Number of Calls': bot.number_of_calls,
          });
        });
      });

      const headers = ['Customer Name', 'Phonebot', 'Number of Calls'];
      let csvContent = '';
      csvContent += headers.join(',') + '\n';
      rows.forEach(row => {
        csvContent += headers.map(h => `"${String(row[h]).replace(/"/g, '""')}"`).join(',') + '\n';
      });

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.setAttribute('download', `calls_history_${this.selectedMonth}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }
};
</script>

<style scoped>
table {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
}

th, td {
  border: 1px solid #ddd !important;
  padding: 10px;
  vertical-align: middle;
  word-wrap: break-word;
  text-align: center;
}

th {
  background-color: #f8f9fa;
  border-top: 2px solid #ddd !important;
}

td:first-child,
th:first-child {
  border-left: 1px solid #ddd !important;
}

tbody tr:last-child td {
  border-bottom: 2px solid #ddd !important;
}

td[rowspan] {
  border-left: 1px solid #ddd !important;
  border-right: 1px solid #ddd !important;
  border-top: 1px solid #ddd !important;
  border-bottom: 1px solid #ddd !important;
}

.pagination .page-link {
  cursor: pointer;
}

/* Controls row styles */
.export-button-container {
  flex: 1;
  display: flex;
  justify-content: flex-start;
}

.search-container {
  flex: 1;
  display: flex;
  justify-content: flex-end;
}

input[type="month"] {
  /* Centered by flex parent */
  max-width: 200px;
}
</style>
